import type { TourStep } from "./AppTutorial";

// Einführungs-Tour für den Admin-Bereich (nur deutsch, wie der ganze
// Admin-Bereich). Schritte mit `feature` filtert AdminLayout gegen das Paket,
// damit die Tour nichts erklärt, was der Mandant gar nicht hat.
export const ADMIN_TOUR_STEPS: TourStep[] = [
  {
    id: "welcome",
    title: { de: "Willkommen im Admin-Bereich" },
    body: [
      { de: "Hier legst du fest, wer welche Schulungen macht, und siehst jederzeit, wo dein Unternehmen steht – vom Schulungsstand einzelner Personen bis zur Abschlussquote aller." },
      { de: "Die Tour zeigt dir in zwei Minuten, wo du was findest." },
    ],
  },
  {
    id: "nav",
    target: '[data-tour="nav"]',
    sidebar: true,
    title: { de: "Die Navigation" },
    body: [
      { de: "Links stehen alle Bereiche. Die Leiste klappt auf, sobald du mit der Maus darüberfährst." },
      { de: "Ein ausgegrauter Punkt mit „Nicht im Plan“ ist in eurem Paket nicht enthalten." },
    ],
  },
  {
    id: "dashboard",
    target: '[data-tour="nav-admin"]',
    sidebar: true,
    title: { de: "Admin-Dashboard" },
    body: [
      { de: "Deine Startseite im Admin-Bereich. Schauen wir sie uns kurz an." },
    ],
  },
  {
    id: "adm-deadline",
    target: '[data-tour="adm-deadline"]',
    optional: true,
    title: { de: "Schulungs-Frist" },
    body: [
      { de: "Bis wann sollen alle ihre Pflicht-Module geschafft haben? Die Frist gilt für das ganze Unternehmen und wird auch den Managern in ihrem Bereich angezeigt." },
      { de: "Rechts siehst du, wie viel schon erledigt ist, und wie viele Tage bis zur Frist bleiben." },
    ],
  },
  {
    id: "adm-metrics",
    target: '[data-tour="adm-metrics"]',
    optional: true,
    title: { de: "Kennzahlen" },
    body: [
      { de: "Phishing-Risiko, wie viele Mitarbeitende AWARE nutzen und in den letzten 14 Tagen aktiv waren, wie viele Trainings abgeschlossen sind und die Abschlussquote." },
      { de: "Die Quote zählt erledigte gegen zugewiesene Module – abgelaufene Abschlüsse zählen nicht mit. Fährst du über eine Kachel, steht dort, wie sie gerechnet wird." },
    ],
  },
  {
    id: "adm-levels",
    target: '[data-tour="adm-levels"]',
    optional: true,
    title: { de: "Sicherheitsniveaus" },
    body: [
      { de: "Wie viele Personen in welchem Plan sind. Der Plan bestimmt, welche Module jemand machen muss." },
    ],
  },
  {
    id: "adm-phishing",
    target: '[data-tour="adm-phishing"]',
    optional: true,
    feature: "phishing_campaigns",
    title: { de: "Risikonutzer & Kampagnen" },
    body: [
      { de: "Links, wer in Phishing-Simulationen am häufigsten geklickt hat – dort lohnt sich eine gezielte Schulung. Rechts die letzten Kampagnen mit ihrer Klickrate." },
    ],
  },
  {
    id: "adm-recent",
    target: '[data-tour="adm-recent"]',
    optional: true,
    title: { de: "Zuletzt abgeschlossen" },
    body: [
      { de: "Die neuesten Trainingsabschlüsse mit Person, Modul, Datum und Ergebnis." },
    ],
  },
  {
    id: "users",
    target: '[data-tour="nav-mitarbeiter"]',
    sidebar: true,
    title: { de: "Mitarbeiter" },
    body: [
      { de: "Alle Mitarbeitenden aus eurem Microsoft 365. Ein Klick auf eine Person zeigt ihre Module, Gruppen, ihren Manager und ob sie schulungspflichtig ist." },
      { de: "Dort machst du ein Konto auch zum Sammelaccount – für Mitarbeitende ohne eigenes Microsoft-Konto, die sich dann mit ihrer Mitarbeiternummer anmelden." },
    ],
  },
  {
    id: "member-numbers",
    target: '[data-tour="nav-mitarbeiternummern"]',
    sidebar: true,
    title: { de: "Mitarbeiternummern" },
    body: [
      { de: "Alle, die sich an einem Sammelaccount mit ihrer Nummer anmelden. Du kannst Listen aus Excel importieren (Nummer, Vorname, Nachname, Team), Namen und Teams bearbeiten und Zugänge sperren oder löschen." },
    ],
  },
  {
    id: "groups",
    target: '[data-tour="nav-gruppen"]',
    sidebar: true,
    title: { de: "Gruppen" },
    body: [
      { de: "Abteilungen aus Microsoft Entra kommen automatisch, eigene Gruppen legst du hier an. Eine Gruppe kann zusätzliche Module und einen eigenen Gruppenmanager bekommen." },
    ],
  },
  {
    id: "risk",
    target: '[data-tour="nav-risiko"]',
    sidebar: true,
    title: { de: "Risikoübersicht" },
    body: [
      { de: "Person für Person, wo das Risiko liegt: Schulungsstand und – wenn ihr Phishing-Simulationen nutzt – das Klickverhalten. Die Liste lässt sich als CSV exportieren." },
    ],
  },
  {
    id: "phishing",
    target: '[data-tour="nav-phishing"]',
    sidebar: true,
    feature: "phishing_campaigns",
    title: { de: "Phishing" },
    body: [
      { de: "Simulierte Phishing-Mails aus Vorlagen: einmalig, geplant oder automatisch wiederkehrend. Wer klickt, landet auf einer Aufklärungsseite statt in einer Falle – und du siehst die Klickrate je Kampagne." },
    ],
  },
  {
    id: "module-plans",
    target: '[data-tour="nav-module-plaene"]',
    sidebar: true,
    title: { de: "Modul-Pläne" },
    body: [
      { de: "Welches Modul in welchem Plan steckt. Die Pläne bauen aufeinander auf: jeder enthält alle Module der Pläne davor." },
      { de: "Außerdem ordnest du hier Module Themen zu und hinterlegst Ansprechpartner, die deine Mitarbeitenden im Modul sehen." },
    ],
  },
  {
    id: "custom-modules",
    target: '[data-tour="nav-eigene-module"]',
    sidebar: true,
    feature: "custom_modules",
    title: { de: "Modulbearbeitung" },
    body: [
      { de: "Eigene Module bauen – mit Text, Bildern, Videos, PDFs und Quiz – oder unsere Standardmodule für euer Unternehmen anpassen. Jede Speicherung wird eine Version, die du ansehen und wiederherstellen kannst." },
      { de: "Überarbeiten wir ein Modul, das ihr angepasst habt, zeigt die Zahl am Menüpunkt, dass es etwas zu prüfen gibt." },
    ],
  },
  {
    id: "reports",
    target: '[data-tour="nav-reports"]',
    sidebar: true,
    feature: "advanced_reports",
    title: { de: "Berichte & Nachweise" },
    body: [
      { de: "Wer hat welche Schulung wann abgeschlossen? Hier siehst du den Stand je Modul, Gruppe und Person und exportierst ihn als Nachweis – zum Beispiel für ein Audit." },
    ],
  },
  {
    id: "notifications-settings",
    target: '[data-tour="nav-benachrichtigungen"]',
    sidebar: true,
    feature: "notifications",
    title: { de: "Benachrichtigungen" },
    body: [
      { de: "Einmal im Monat eine automatische Statusmail: Mitarbeitende nur, wenn bei ihnen etwas offen ist, Vorgesetzte mit dem Stand ihres Teams. Vorschau und Testmail findest du direkt dort." },
    ],
  },
  {
    id: "settings",
    target: '[data-tour="nav-einstellungen"]',
    sidebar: true,
    title: { de: "Einstellungen" },
    body: [
      { de: "Euer Paket, euer Logo, wie lange Module gültig sind, ob es den Wissenstest gibt, und die Datenschutz-Fristen für gelöschte oder inaktive Konten." },
    ],
  },
  {
    id: "employee-view",
    target: '[data-tour="nav-employee"]',
    sidebar: true,
    title: { de: "Mitarbeiter-Ansicht" },
    body: [
      { de: "So sehen deine Mitarbeitenden AWARE. Dort machst du auch deine eigenen Schulungen." },
    ],
  },
  {
    id: "bell",
    target: '[data-tour="notifications"]',
    title: { de: "Hinweise" },
    body: [
      { de: "Die Glocke meldet sich, wenn etwas deine Aufmerksamkeit braucht – etwa eine Kampagne mit auffällig vielen Klicks oder eine neue Fassung eines Moduls, das ihr angepasst habt." },
    ],
  },
  {
    id: "help",
    target: '[data-tour="help"]',
    title: { de: "Hilfe jederzeit" },
    body: [
      { de: "Über das Fragezeichen öffnest du diese Tour jederzeit wieder." },
    ],
  },
  {
    id: "finish",
    title: { de: "Womit du anfangen kannst" },
    body: [
      { de: "Ein guter Start: in den Modul-Plänen prüfen, welche Module in welchem Plan stecken, allen ihr Sicherheitsniveau geben und auf dem Dashboard eine Schulungs-Frist setzen." },
    ],
  },
];
